import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Container, Typography, Box, CircularProgress, Card, CardContent, Divider, Paper } from '@mui/material';
import { Google, GitHub, Login, Security, Speed, Group } from '@mui/icons-material';
import { signInWithPopup, GoogleAuthProvider, GithubAuthProvider, linkWithCredential, getAdditionalUserInfo } from 'firebase/auth';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, githubProvider } from '../firebase';
import { createUserProfile } from '../services/userService';

const Auth = () => {
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  const googleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      console.log("Google login successful:", result.user);
      
      // Create or update user profile
      try {
        await createUserProfile(result.user);
      } catch (profileError) {
        console.error("Error creating user profile:", profileError);
      }
      
    } catch (error) {
      // console.error("Google login error:", error);
      // console.error("Error code:", error.code);
      // console.error("Error message:", error.message);
      
      if (error.code === 'auth/account-exists-with-different-credential') {
        alert(
          'An account with this email already exists with a different sign-in method. Please use the original sign-in method.'
        );
      } else if (error.code === 'auth/email-already-in-use') {
        alert(
          'This email is already registered with a different sign-in method. Please use your original sign-in method or contact support.'
        );
      } else if (error.code === 'auth/popup-closed-by-user') {
        // console.log("Google login popup was closed by user");
      } else if (error.code === 'auth/cancelled-popup-request') {
        // console.log("Google login popup was cancelled");
      } else {
        alert('Google login failed. Please try again.');
      }
    }
  };

  const githubLogin = async () => {
    try {
      const result = await signInWithPopup(auth, githubProvider);
      console.log("GitHub login successful:", result.user);
      
      // Create or update user profile
      try {
        await createUserProfile(result.user);
      } catch (profileError) {
        console.error("Error creating user profile:", profileError);
      }
      
    } catch (error) {
      // console.error("GitHub login error:", error);
      // console.error("Error code:", error.code);
      // console.error("Error message:", error.message);
      
      if (error.code === 'auth/account-exists-with-different-credential') {
        // Handle account linking
        alert(
          'An account with this email already exists. Please sign in with your existing method first, then visit your Profile page to link additional accounts.'
        );
      } else if (error.code === 'auth/email-already-in-use') {
        alert(
          'This email is already registered with a different sign-in method. Please use your original sign-in method or contact support.'
        );
      } else if (error.code === 'auth/popup-closed-by-user') {
        // console.log("GitHub login popup was closed by user");
      } else if (error.code === 'auth/cancelled-popup-request') {
        // console.log("GitHub login popup was cancelled");
      } else {
        alert('GitHub login failed. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}
        >
          <CircularProgress size={50} />
          <Typography variant="h6" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom
          sx={{ 
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            mb: 2
          }}
        >
          Welcome to FormPulse
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
          Sign in to start creating and managing your forms
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Sign In Card */}
        <Card 
          elevation={8} 
          sx={{ 
            flex: 1,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0'
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Login sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Sign In
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose your preferred sign-in method
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Button
                variant="contained"
                startIcon={<Google />}
                onClick={googleLogin}
                size="large"
                fullWidth
                sx={{
                  bgcolor: '#4285F4',
                  '&:hover': { bgcolor: '#357ABD' },
                  color: 'white',
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}
              >
                Continue with Google
              </Button>
              
              <Button
                variant="contained"
                startIcon={<GitHub />}
                onClick={githubLogin}
                size="large"
                fullWidth
                sx={{
                  bgcolor: '#24292e',
                  '&:hover': { bgcolor: '#1a1e22' },
                  color: 'white',
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}
              >
                Continue with GitHub
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Features Card */}
        <Card 
          elevation={8} 
          sx={{ 
            flex: 1,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              Why Choose FormPulse?
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Security sx={{ fontSize: 32, opacity: 0.9 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Secure & Reliable
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Enterprise-grade security with OAuth authentication
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Speed sx={{ fontSize: 32, opacity: 0.9 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Lightning Fast
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Create and deploy forms in minutes, not hours
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Group sx={{ fontSize: 32, opacity: 0.9 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Team Collaboration
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Share forms and collaborate with your team seamlessly
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Divider sx={{ my: 3, bgcolor: 'rgba(255,255,255,0.3)' }} />
            
            <Typography variant="body2" sx={{ textAlign: 'center', opacity: 0.9 }}>
              Join thousands of users who trust FormPulse for their form management needs
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Auth;